import React from 'react';
import { numbered, sectionTotal, paperTotal } from './paperUtils';
import { Rich, CSS, FONT, watermarkBg } from './previewStyles';

function Options({ q }) {
  if (q.type === 'match') {
    const rows = (q.options || []).map((o) => o.split('::').map((x) => x.trim()));
    return (
      <table className="qpb-match">
        <thead><tr><th>Column A</th><th>Column B</th></tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i}><td>{i + 1}. {r[0]}</td><td>{String.fromCharCode(97 + i)}. {r[1] || ''}</td></tr>)}</tbody>
      </table>
    );
  }
  if (q.type !== 'mcq') return null;
  const opts = (q.options || []).filter((o) => o !== '');
  return (
    <div className={`qpb-opts ${opts.length === 2 ? 'two' : ''}`}>
      {opts.map((o, i) => <div key={i}><b>({String.fromCharCode(65 + i)})</b> <Rich text={o} /></div>)}
    </div>
  );
}

function Question({ q, keyMode }) {
  return (
    <div className="aip-block">
      <div className="qpb-q">
        <div className="qpb-qn">{q.questionNumber}.</div>
        <div className="qpb-qb">
          <Rich text={q.text} />
          <Options q={q} />
          {q.hasOrChoice && q.orQuestionText && (
            <><div className="qpb-or">— OR —</div><Rich text={q.orQuestionText} /></>
          )}
          {keyMode && (q.correctAnswer || q.hint || q.rubric) && (
            <div className="qpb-ans">
              {q.correctAnswer && <div><b>Ans:</b> <Rich text={q.correctAnswer} /></div>}
              {q.hint && <div><b>Hint:</b> <Rich text={q.hint} /></div>}
              {q.rubric && <div><b>Marking scheme:</b> <Rich text={q.rubric} /></div>}
            </div>
          )}
        </div>
        <div className="qpb-qm">[{q.marks}]</div>
      </div>
    </div>
  );
}

// The forwarded ref is the element exported to PDF / print (same markup for paper and answer key).
const PaperPreview = React.forwardRef(function PaperPreview({ paper, keyMode = false }, ref) {
  const h = paper.headerConfig || {};
  const secs = numbered(paper.sections || []);
  const total = paperTotal(secs);
  const max = paper.maxMarksLocked ? paper.maxMarks : total;
  const two = h.columnLayout === 'double';
  const wm = watermarkBg(h.watermarkText);
  return (
    <div ref={ref} className="qpb-sheet aip-sheet" style={{ fontSize: FONT[h.fontSize] || FONT.md, backgroundImage: wm || 'none', backgroundRepeat: 'repeat-y' }}>
      <style>{CSS}</style>
      <div className="qpb-head aip-block">
        <div className="qpb-head-row">
          {h.showLogo && h.logoUrl && <img src={h.logoUrl} alt="" crossOrigin="anonymous" />}
          <div>
            <h1 className="qpb-school">{h.schoolName || 'School Name'}</h1>
            {h.affiliation && <div style={{ fontSize: '.9em' }}>{h.affiliation}</div>}
          </div>
        </div>
      </div>
      {h.showConfidential && <div className="qpb-conf aip-block">CONFIDENTIAL</div>}
      <div className="qpb-banner aip-block">{h.examTitle}{paper.session ? ` · Session ${paper.session}` : ''}{keyMode ? ' — ANSWER KEY (TEACHER COPY)' : ''}</div>
      <div className="qpb-meta aip-block">
        <div><b>Class:</b> {paper.classId}{paper.section ? ` - ${paper.section}` : ''}</div>
        <div><b>Subject:</b> {paper.subject}</div>
        <div><b>Time Allowed:</b> {paper.timeAllowed}</div>
        <div><b>Maximum Marks:</b> {max}</div>
      </div>
      {!keyMode && (h.customInstructions || []).length > 0 && (
        <div className="qpb-instr aip-block">
          <b>General Instructions:</b>
          <ol>{h.customInstructions.map((t, i) => <li key={i}><Rich text={t} /></li>)}</ol>
        </div>
      )}
      {secs.map((s) => (
        <div key={s.id || s.sectionTitle}>
          <div className="qpb-sec aip-block">{s.sectionTitle}{sectionTotal(s) ? ` (${sectionTotal(s)} Marks)` : ''}</div>
          {s.sectionInstructions && <div className="qpb-sub aip-block"><Rich text={s.sectionInstructions} /></div>}
          <div className={two ? 'qpb-grid2' : ''}>
            {s.questions.map((q) => <Question key={q.id || q.questionNumber} q={q} keyMode={keyMode} />)}
          </div>
        </div>
      ))}
      <div className="qpb-foot aip-block">— END OF {keyMode ? 'ANSWER KEY' : 'PAPER'} —</div>
    </div>
  );
});

export default PaperPreview;
